<script setup lang="ts">
import { Eye as EyeIcon, EyeOff as EyeOffIcon, LogIn as LogInIcon } from '@lucide/vue'
import { storeToRefs } from 'pinia'
import { useField, useForm } from 'vee-validate'
import { ref, onMounted, nextTick } from 'vue'

import BaseSpinner from '@/components/ui/BaseSpinner.vue'
import { useAuthStore } from '@/stores/authStore'

interface Emits {
  success: []
  switchToSignup: []
  switchToReset: [email: string]
}

const emit = defineEmits<Emits>()

// Auth store
const authStore = useAuthStore()
const { isLoading, error } = storeToRefs(authStore)

// Password visibility
const showPassword = ref(false)

// Refs for focus management
const emailInputRef = ref<HTMLInputElement | null>(null)
const submitButtonRef = ref<HTMLButtonElement | null>(null)

// Form setup
const { handleSubmit, resetForm } = useForm()

// Email validation function
const validateEmail = (value: string) => {
  if (!value) {
    return 'Email is required'
  }
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(value)) {
    return 'Please enter a valid email'
  }
  return true
}

// Password validation function
const validatePassword = (value: string) => {
  if (!value) {
    return 'Password is required'
  }
  return true
}

// Form fields
const { value: email, errorMessage: emailError, meta: emailMeta } = useField('email', validateEmail)

const {
  value: password,
  errorMessage: passwordError,
  meta: passwordMeta,
} = useField('password', validatePassword)

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value
}

// Handle form submission
const onSubmit = handleSubmit(async (values) => {
  authStore.clearError()

  const success = await authStore.login({
    email: values.email,
    password: values.password,
  })

  if (success) {
    resetForm()
    emit('success')
  }
})

// Switch to signup
const handleSwitchToSignup = () => {
  resetForm()
  authStore.clearError()
  emit('switchToSignup')
}

// Switch to password reset
const handleSwitchToReset = () => {
  authStore.clearError()
  emit('switchToReset', email.value || '')
}

// Clear errors when component mounts and focus first input
onMounted(() => {
  authStore.clearError()

  // Focus the first input after component is mounted
  nextTick(() => {
    if (emailInputRef.value) {
      emailInputRef.value.focus()
    }
  })
})
</script>

<template>
  <!-- Add live region for form status announcements -->
  <div id="login-status" class="sr-only" aria-live="polite" aria-atomic="true">
    <span v-if="isLoading">Logging in, please wait...</span>
    <span v-else-if="error">{{ error }}</span>
  </div>

  <form @submit.prevent="onSubmit" class="space-y-4" novalidate>
    <!-- Email field -->
    <div>
      <label for="login-email" class="block text-sm font-medium text-gray-700 mb-2">
        Email Address <span class="text-red-500" aria-label="required">*</span>
      </label>
      <input
        id="login-email"
        ref="emailInputRef"
        v-model="email"
        type="email"
        autocomplete="email"
        required
        class="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 transition-colors"
        :class="{
          'border-red-300 focus:ring-red-500': emailError,
          'border-green-300 focus:ring-green-500': emailMeta.valid && emailMeta.touched,
          'border-gray-300 focus:ring-primary-500':
            !emailError && (!emailMeta.valid || !emailMeta.touched),
        }"
        placeholder="Enter your email address"
        :disabled="isLoading"
        :aria-invalid="emailError ? 'true' : 'false'"
        :aria-describedby="emailError ? 'login-email-error' : 'login-email-help'"
      />
      <div v-if="!emailError" id="login-email-help" class="sr-only">
        Enter your email address to log in
      </div>
      <div v-if="emailError" id="login-email-error" class="mt-1 text-sm text-red-600" role="alert">
        {{ emailError }}
      </div>
    </div>

    <!-- Password field -->
    <div>
      <label for="login-password" class="block text-sm font-medium text-gray-700 mb-2">
        Password <span class="text-red-500" aria-label="required">*</span>
      </label>
      <div class="relative">
        <input
          id="login-password"
          v-model="password"
          :type="showPassword ? 'text' : 'password'"
          autocomplete="current-password"
          required
          class="w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 transition-colors"
          :class="{
            'border-red-300 focus:ring-red-500': passwordError,
            'border-green-300 focus:ring-green-500': passwordMeta.valid && passwordMeta.touched,
            'border-gray-300 focus:ring-primary-500':
              !passwordError && (!passwordMeta.valid || !passwordMeta.touched),
          }"
          placeholder="Enter your password"
          :disabled="isLoading"
          :aria-invalid="passwordError ? 'true' : 'false'"
          :aria-describedby="passwordError ? 'login-password-error' : 'login-password-help'"
        />
        <button
          type="button"
          @click="togglePasswordVisibility"
          :class="[
            // Position & layout
            'absolute',
            'right-3',
            'top-1/2',
            'transform',
            '-translate-y-1/2',
            'p-1',
            // Colors & interaction
            'text-gray-400',
            'hover:text-gray-600',
            'hover:cursor-pointer',
            // Focus state
            'focus:outline-none',
            'focus:ring-2',
            'focus:ring-primary-500',
            'focus:ring-offset-2',
            // Shape
            'rounded-md',
          ]"
          :aria-label="showPassword ? 'Hide password' : 'Show password'"
          :aria-pressed="showPassword"
          :disabled="isLoading"
          tabindex="0"
        >
          <EyeOffIcon v-if="showPassword" class="w-5 h-5" />
          <EyeIcon v-else class="w-5 h-5" />
        </button>
      </div>
      <div v-if="!passwordError" id="login-password-help" class="sr-only">
        Enter your password to log in
      </div>
      <div
        v-if="passwordError"
        id="login-password-error"
        class="mt-1 text-sm text-red-600"
        role="alert"
      >
        {{ passwordError }}
      </div>
    </div>

    <!-- Error message -->
    <div
      v-if="error"
      class="p-3 bg-red-50 border border-red-200 rounded-md"
      role="alert"
      aria-live="polite"
    >
      <p class="text-sm text-red-600">{{ error }}</p>
    </div>

    <!-- Submit button -->
    <button
      ref="submitButtonRef"
      type="submit"
      :disabled="isLoading"
      :aria-describedby="isLoading ? 'login-loading-status' : undefined"
      :class="[
        // Layout & sizing
        'w-full',
        'flex',
        'items-center',
        'justify-center',
        'px-4',
        'py-2',
        // Colors & states
        'bg-primary-600',
        'hover:bg-primary-700',
        'disabled:bg-primary-400',
        'text-white',
        // Typography & effects
        'font-medium',
        'rounded-md',
        'transition-colors',
        // Focus & interaction
        'focus:outline-none',
        'focus:ring-2',
        'focus:ring-primary-500',
        'focus:ring-offset-2',
        'hover:cursor-pointer',
      ]"
    >
      <LogInIcon v-if="!isLoading" class="w-4 h-4 mr-2" aria-hidden="true" />
      <BaseSpinner v-else class="mr-2" aria-hidden="true" />
      {{ isLoading ? 'Logging in...' : 'Log in' }}
    </button>
    <div v-if="isLoading" id="login-loading-status" class="sr-only">
      Please wait while we log you in
    </div>

    <!-- Footer links -->
    <div class="space-y-3 text-center">
      <button
        type="button"
        @click="handleSwitchToReset"
        :class="[
          // Typography & colors
          'text-sm',
          'text-primary-600',
          'hover:text-primary-800',
          'underline',
          // Interaction
          'hover:cursor-pointer',
          // Focus state
          'focus:outline-none',
          'focus:ring-2',
          'focus:ring-primary-500',
          'focus:ring-offset-2',
          // Shape
          'rounded-sm',
        ]"
        :disabled="isLoading"
        aria-describedby="reset-password-help"
      >
        Forgot your password?
      </button>
      <div id="reset-password-help" class="sr-only">Click to reset your password via email</div>

      <div class="flex items-center justify-center space-x-1 text-sm">
        <span class="text-gray-600">Don't have an account?</span>
        <button
          type="button"
          @click="handleSwitchToSignup"
          :class="[
            // Colors & interaction
            'text-primary-600',
            'hover:text-primary-800',
            'font-medium',
            'hover:cursor-pointer',
            // Focus state
            'focus:outline-none',
            'focus:ring-2',
            'focus:ring-primary-500',
            'focus:ring-offset-2',
            // Shape
            'rounded-sm',
          ]"
          :disabled="isLoading"
          aria-describedby="signup-help"
        >
          Sign up
        </button>
      </div>
      <div id="signup-help" class="sr-only">Click to create a new account</div>
    </div>
  </form>
</template>
